import React, { useEffect, useRef, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Form } from '@/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Users,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Plus,
  X,
} from 'lucide-react';
import { useAssignableRoles } from '@/hooks/submissions/useAssignableRoles';
import {
  useBulkInviteUsers,
  type BulkInvitationResult,
} from '@/hooks/submissions/useBulkInviteUsers';

export const MAX_BULK_INVITATIONS = 25;

const bulkInviteRowSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  role_key: z.string().min(1, 'Please select a role'),
});

const bulkInviteFormSchema = z.object({
  invitations: z
    .array(bulkInviteRowSchema)
    .min(1, 'Add at least one email')
    .max(
      MAX_BULK_INVITATIONS,
      `You can invite up to ${MAX_BULK_INVITATIONS} people at a time`
    ),
});

type BulkInviteFormData = z.infer<typeof bulkInviteFormSchema>;

interface BulkInviteUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The API path for posting bulk invitations, e.g. /submissions/:id/bulk_invitations */
  bulkInvitationsPath: string;
  /** The resource base path for fetching assignable roles, e.g. /studies/:id */
  assignableRolesPath: string;
  onSuccess?: () => void;
}

const BulkInviteUsersModal: React.FC<BulkInviteUsersModalProps> = ({
  isOpen,
  onClose,
  bulkInvitationsPath,
  assignableRolesPath,
  onSuccess,
}) => {
  const [submissionError, setSubmissionError] = useState('');
  // Per-row outcome of the last submit, keyed by the react-hook-form field id
  // (not the array index) so removing a row can't shift results onto the
  // wrong email. Rows whose result is 'invited' are locked and skipped on
  // any subsequent submit so a retry only re-sends the rows that failed.
  const [rowResults, setRowResults] = useState<
    Record<string, BulkInvitationResult>
  >({});
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { roles, isLoading: isLoadingRoles } =
    useAssignableRoles(assignableRolesPath);
  const bulkInvite = useBulkInviteUsers(bulkInvitationsPath);

  const form = useForm<BulkInviteFormData>({
    resolver: zodResolver(bulkInviteFormSchema),
    defaultValues: {
      invitations: [{ email: '', role_key: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'invitations',
  });

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Don't let the auto-close timer fire against an unmounted modal.
  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const resetState = () => {
    clearCloseTimer();
    form.reset({ invitations: [{ email: '', role_key: '' }] });
    setSubmissionError('');
    setRowResults({});
  };

  const handleClose = () => {
    if (!bulkInvite.isPending) {
      resetState();
      onClose();
    }
  };

  const isRowSent = (fieldId: string) =>
    rowResults[fieldId]?.status === 'invited';

  const handleRemoveRow = (index: number, fieldId: string) => {
    remove(index);
    setRowResults(prev => {
      if (!(fieldId in prev)) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const onSubmit = async (data: BulkInviteFormData) => {
    setSubmissionError('');

    // Only rows that haven't already been sent successfully.
    const pendingRows = fields
      .map((field, index) => ({ id: field.id, row: data.invitations[index] }))
      .filter(({ id }) => !isRowSent(id));

    if (pendingRows.length === 0) return;

    // Drop stale failure messages for the rows about to be retried.
    setRowResults(prev => {
      const next = { ...prev };
      pendingRows.forEach(({ id }) => delete next[id]);
      return next;
    });

    try {
      const response = await bulkInvite.mutateAsync(
        pendingRows.map(({ row }) => row)
      );

      // The server returns one result per submitted row, in the same order.
      setRowResults(prev => {
        const next = { ...prev };
        response.results.forEach((result, i) => {
          next[pendingRows[i].id] = result;
        });
        return next;
      });

      // Refresh the caller's user/invitation list right away regardless of
      // partial failure, since some rows may have succeeded.
      onSuccess?.();

      if (response.failed_count === 0) {
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
          closeTimerRef.current = null;
          resetState();
          onClose();
        }, 2000);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      setSubmissionError(errorMessage);
    }
  };

  const isSubmitting = bulkInvite.isPending;
  const hasResults = Object.keys(rowResults).length > 0;
  const sentCount = fields.filter(field => isRowSent(field.id)).length;
  const hasFailedRows = fields.some(
    field => rowResults[field.id]?.status === 'failed'
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Invite Users
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            className="space-y-6"
          >
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="flex-1 text-sm font-medium text-gray-700">
                  Email *
                </span>
                <span className="flex-1 text-sm font-medium text-gray-700">
                  Role *
                </span>
                <span className="w-4" aria-hidden="true" />
              </div>

              {fields.map((field, index) => {
                const rowErrors = form.formState.errors.invitations?.[index];
                const rowResult = rowResults[field.id];
                const rowSent = rowResult?.status === 'invited';

                return (
                  <div key={field.id} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 space-y-1">
                        <Label
                          htmlFor={`invitations.${index}.email`}
                          className="sr-only"
                        >
                          Email
                        </Label>
                        <Input
                          id={`invitations.${index}.email`}
                          type="email"
                          placeholder="Enter email address"
                          {...form.register(`invitations.${index}.email`)}
                          disabled={isSubmitting || rowSent}
                        />
                        {rowErrors?.email && (
                          <p className="text-red-500 text-sm">
                            {rowErrors.email.message}
                          </p>
                        )}
                      </div>

                      <div className="flex-1 space-y-1">
                        <Label
                          htmlFor={`invitations.${index}.role_key`}
                          className="sr-only"
                        >
                          Role
                        </Label>
                        <Select
                          value={form.watch(`invitations.${index}.role_key`)}
                          onValueChange={value =>
                            form.setValue(
                              `invitations.${index}.role_key`,
                              value
                            )
                          }
                          disabled={isSubmitting || isLoadingRoles || rowSent}
                        >
                          <SelectTrigger id={`invitations.${index}.role_key`}>
                            <SelectValue
                              placeholder={
                                isLoadingRoles ? 'Loading...' : 'Select role'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.map(role => (
                              <SelectItem key={role.key} value={role.key}>
                                {role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {rowErrors?.role_key && (
                          <p className="text-red-500 text-sm">
                            {rowErrors.role_key.message}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        aria-label={`Remove row ${index + 1}`}
                        className="shrink-0 text-gray-400 hover:text-red-500 disabled:opacity-30"
                        onClick={() => handleRemoveRow(index, field.id)}
                        disabled={
                          isSubmitting || rowSent || fields.length === 1
                        }
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {rowResult && (
                      <div
                        className={`flex items-center gap-2 text-sm ${
                          rowResult.status === 'invited'
                            ? 'text-green-700'
                            : 'text-red-600'
                        }`}
                      >
                        {rowResult.status === 'invited' ? (
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                        ) : (
                          <XCircle className="h-4 w-4 shrink-0" />
                        )}
                        <span>
                          {rowResult.status === 'invited'
                            ? 'Invitation sent'
                            : rowResult.error}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {form.formState.errors.invitations?.root && (
              <p className="text-red-500 text-sm">
                {form.formState.errors.invitations.root.message}
              </p>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append({ email: '', role_key: '' })}
              disabled={isSubmitting || fields.length >= MAX_BULK_INVITATIONS}
              className="flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Add another
            </Button>

            {hasResults && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-green-700">
                  {sentCount} of {fields.length} invitation
                  {fields.length === 1 ? '' : 's'} sent successfully.
                  {hasFailedRows &&
                    ' Fix the failed rows above and try again, or remove them.'}
                </AlertDescription>
              </Alert>
            )}

            {submissionError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submissionError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary-500 hover:bg-primary-600 text-white"
              >
                {isSubmitting ? 'Sending...' : 'Send Invitations'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default BulkInviteUsersModal;
